import { SuppressedPeriod } from '@core/models/domain/school-calendar.domain';

export interface TaskDefinition {
  id?: string;
  children?: Array<string>; // IDs of pupils or groups of pupils
  title: string;
  description: string;
  approvalMethod?: ApprovalMethod;
  schoolInfo?: TaskSchoolInfo;
  status: TaskDefinitionStatus;
  repeatSelectedDays: Array<DgDayOfWeek>;         
  startAt: Date;
  endAt: Date;
  cancelledTaskDates?: Array<Date>;
  creationDatetime?: Date;
  validityStartDate?: Date;
  validityEndDate?: Date;
  timezone?: string;
  createdBy: string;
  followedBy: Array<string>;
  /**
   * Days on which the school calendar suspends this restriction, computed by the backend.
   *
   * <p>Authoritative: the same calculation is what the scheduler launches from, so the app must
   * render this rather than re-deriving it from the calendar. Absent on family restrictions and on
   * schools with no exceptions.</p>
   */
  suppressedPeriods?: Array<SuppressedPeriod>;
}

export interface TaskExecution {
  id: string;
  taskDefinitionId: string;  
  status: TaskExecutionStatus;
  comment?: string;
  taskApproval?: TaskApproval;
  scheduledAt: Date;
  scheduledEndTime: Date;
  startedAt: Date;
  endedAt: Date;
  creationDatetime: Date;
  lastUpdateDatetime: Date;
}
export interface TaskSchoolInfo {
    schoolAccountId: string;
    schoolName?: string;
    assignedGroups: Array<string>; // IDs of groups of pupils
}

export enum TaskExecutionStatus {
  NOT_STARTED = "Upcoming",
  IN_PROGRESS = "In progress",
  COMPLETED = "Completed",
  RESTRICTION_CANCELLED = "Restriction cancelled",
  CANCELLED = "Cancelled",
  SKIPPED = "Skipped",
}

export enum TaskDefinitionStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum ApprovalMethod {
  CALL = "CALL",
  VIDEO = "VIDEO",
  PICTURE = "PICTURE",
  GPS = "GPS",
}

export class DgDayOfWeek {
  static readonly MONDAY = new DgDayOfWeek(0, "M", "Mon", "Monday");
  static readonly TUESDAY = new DgDayOfWeek(1, "T", "Tue", "Tuesday");
  static readonly WEDNESDAY = new DgDayOfWeek(2, "W", "Wed", "Wednesday");
  static readonly THURSDAY = new DgDayOfWeek(3, "T", "Thu", "Thursday");
  static readonly FRIDAY = new DgDayOfWeek(4, "F", "Fri", "Friday");
  static readonly SATURDAY = new DgDayOfWeek(5, "S", "Sat", "Saturday");
  static readonly SUNDAY = new DgDayOfWeek(6, "S", "Sun", "Sunday");

  private constructor(
    public index: number,
    public symbol: string,
    public shortName,
    public label: string
  ) {}
}
export class TaskApproval {
  fileName: string;
}

export enum ParentCancellationReason {
  ABSENT_DUE_TO_ILLNESS = 'ABSENT_DUE_TO_ILLNESS',
  APPOINTMENT = 'APPOINTMENT',
  OTHER = 'OTHER',
}

export const PARENT_CANCELLATION_REASON_LABELS: Record<ParentCancellationReason, string> = {
  [ParentCancellationReason.ABSENT_DUE_TO_ILLNESS]: 'Absent due to illness',
  [ParentCancellationReason.APPOINTMENT]: 'Appointment',
  [ParentCancellationReason.OTHER]: 'Other',
};

/**
 * What a parent's cancellation reason reads as: the label, with the parent's own words after
 * "Other" (DIGI-1863). Older records and other reasons carry no text.
 */
export function parentCancellationReasonText(reason: string, reasonDetail?: string | null): string {
  const label = PARENT_CANCELLATION_REASON_LABELS[reason as ParentCancellationReason] || reason;
  const detail = reason === ParentCancellationReason.OTHER ? reasonDetail?.trim() : '';
  return detail ? `${label}: ${detail}` : label;
}

export interface ParentTaskCancellation {
  id: string;
  taskDefinitionId: string;
  taskExecutionId?: string;
  childId: string;
  childName: string;
  taskName: string;
  reason: ParentCancellationReason;
  /** The parent's own words after "Other" (DIGI-1863); absent on older records. */
  reasonDetail?: string | null;
  cancelledBy: string;
  cancelledByName: string;
  cancelledDate: Date;
  creationDatetime: Date;
  schoolAccountId: string;
}